import React from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';

/**
 * Notifications opt-in card for Profile / Settings.
 *
 * State-driven UI:
 *   unsupported → "Available on the iOS / Android app" hint (web users)
 *   idle        → "Enable notifications" primary CTA
 *   requesting  → spinner + "Asking your device…"
 *   enabled     → green pill + "Turn off" ghost button
 *   denied      → "Re-enable in Settings" (helper already opens OS settings)
 *   error       → "Try again"
 */
const NotificationsCard = () => {
  const { state, enable, disable } = usePushNotifications();

  return (
    <div
      className="p-6 rounded-2xl bg-card border border-border/50"
      data-testid="notifications-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          {state === 'enabled' ? (
            <BellRing className="h-5 w-5 text-primary" />
          ) : state === 'denied' ? (
            <BellOff className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Bell className="h-5 w-5 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Notifications
          </h3>
          <p className="text-sm text-foreground mb-3">
            {state === 'enabled' && 'You\'ll get pings when your listings are verified, matches finish, and shared routes go viral.'}
            {state === 'requesting' && 'Asking your device for permission…'}
            {(state === 'idle' || state === 'error') && 'Turn on pushes to know when your matches are ready and when others share your routes.'}
            {state === 'denied' && 'Notifications are turned off for TheUnityBlaze on this device.'}
            {state === 'unsupported' && 'This browser doesn\'t support push notifications. Use Chrome, Edge, or Firefox on desktop, or install the mobile app.'}
          </p>

          {state === 'idle' && (
            <Button
              size="sm"
              onClick={enable}
              className="rounded-full"
              data-testid="enable-notifications-btn"
            >
              <Bell className="h-4 w-4 mr-2" />
              Enable notifications
            </Button>
          )}

          {state === 'requesting' && (
            <Button size="sm" disabled className="rounded-full" data-testid="enable-notifications-loading">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Asking…
            </Button>
          )}

          {state === 'enabled' && (
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"
                data-testid="notifications-enabled-pill"
              >
                <BellRing className="h-3 w-3" />
                Enabled
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={disable}
                data-testid="disable-notifications-btn"
              >
                Turn off
              </Button>
            </div>
          )}

          {state === 'denied' && (
            <Button
              size="sm"
              variant="outline"
              onClick={enable}
              className="rounded-full"
              data-testid="reenable-notifications-btn"
            >
              Open Settings
            </Button>
          )}

          {state === 'error' && (
            <Button
              size="sm"
              variant="outline"
              onClick={enable}
              className="rounded-full"
              data-testid="retry-notifications-btn"
            >
              Try again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsCard;
